// src/components/jobseeker/JobSeekerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaBriefcase, FaClock, FaIdCard, FaUserEdit,
  FaStar, FaEnvelope, FaFileAlt, FaMapMarkerAlt
} from 'react-icons/fa';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getJobSeekerReviews } from '../../services/enhancedFirestore';

const JobSeekerDashboard = () => {
  const { userProfile, currentUser } = useAuth();
  console.log('JobSeekerDashboard currentUser:', currentUser);
  console.log('JobSeekerDashboard userProfile:', userProfile);
  const [stats, setStats] = useState({
    applications: 0, interviews: 0, savedJobs: 0, reviews: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [errorActivities, setErrorActivities] = useState(null);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [loadingAppliedJobs, setLoadingAppliedJobs] = useState(true);
  const [errorAppliedJobs, setErrorAppliedJobs] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [providerNames, setProviderNames] = useState({});
  const navigate = useNavigate();
  
  // In a real application, fetch data from an API
  useEffect(() => {
    // Placeholder - no functionality needed here
  }, []);
  
  useEffect(() => {
    if (!currentUser) return;
    const fetchActivities = async () => {
      try {
        setLoadingActivities(true);
        const q = query(
          collection(db, "messages"),
          where("participants", "array-contains", currentUser.uid),
          orderBy("createdAt", "desc"),
          limit(20)
        );
        const querySnapshot = await getDocs(q);
        const activities = [];
        const userIds = new Set();
        querySnapshot.forEach((doc) => {
          const message = doc.data();
          // Add both sender and receiver IDs to the set
          userIds.add(message.senderId);
          message.participants.forEach(id => userIds.add(id));
          activities.push({
            id: doc.id,
            type: 'message',
            senderId: message.senderId,
            receiverId: message.participants.find(id => id !== message.senderId),
            content: message.text,
            timestamp: message.createdAt,
            icon: '📬'
          });
        });
        // Fetch user names in batch
        const userIdArr = Array.from(userIds);
        const userNames = {};
        if (userIdArr.length > 0) {
          const usersRef = collection(db, "users");
          const usersQuery = query(usersRef, where("uid", "in", userIdArr));
          const usersSnapshot = await getDocs(usersQuery);
          usersSnapshot.forEach((doc) => {
            const user = doc.data();
            userNames[user.uid] = user.displayName || user.name || user.email || user.uid;
          });
        }
        // Attach user names to activities
        const activitiesWithNames = activities.map(activity => ({
          ...activity,
          senderName: userNames[activity.senderId] || activity.senderId,
          receiverName: userNames[activity.receiverId] || activity.receiverId
        }));
        // Group by senderId and receiverId and keep only the most recent message per pair
        const uniqueActivities = [];
        const seenPairs = new Set();
        for (const activity of activitiesWithNames) {
          // Create a consistent pair key regardless of who sent the message
          const pairKey = [activity.senderId, activity.receiverId].sort().join('-');
          if (!seenPairs.has(pairKey)) {
            uniqueActivities.push(activity);
            seenPairs.add(pairKey);
          }
          if (uniqueActivities.length >= 5) break;
        }
        setRecentActivities(uniqueActivities);
      } catch (error) {
        setErrorActivities(error);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    // Fetch applications count
    const fetchApplicationsCount = async () => {
      const q = query(collection(db, 'applications'), where('jobSeekerUid', '==', currentUser.uid));
      const snapshot = await getDocs(q);
      setStats(prev => ({ ...prev, applications: snapshot.size }));
    };
    fetchApplicationsCount();
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    // Fetch reviews count
    const fetchReviewsCount = async () => {
      const reviews = await getJobSeekerReviews(currentUser.uid);
      setStats(prev => ({ ...prev, reviews: reviews.length }));
    };
    fetchReviewsCount();
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    const fetchAppliedJobs = async () => {
      setLoadingAppliedJobs(true);
      setErrorAppliedJobs(null);
      try {
        // Fetch applications for this jobseeker
        const q = query(collection(db, 'applications'), where('jobSeekerUid', '==', currentUser.uid), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const applications = snapshot.docs.map(docSnap => {
          const appData = docSnap.data();
          return {
            id: docSnap.id,
            jobTitle: appData.jobTitle || 'N/A',
            providerUid: appData.providerUid || null,
            parentUid: appData.parentUid || null,
            appliedAt: appData.createdAt,
            jobId: appData.jobId,
            status: appData.status || 'N/A',
          };
        });
        setAppliedJobs(applications);

        // Fetch names for 'Posted By' (provider or parent)
        const uniqueUids = [...new Set(applications.map(app => app.providerUid ? app.providerUid : app.parentUid).filter(uid => uid))];
        const namesMap = {};
        for (const app of applications) {
          let uid = app.providerUid ? app.providerUid : app.parentUid;
          if (!uid || namesMap[uid]) continue;
          try {
            let userDoc = null;
            if (app.providerUid) {
              userDoc = await getDoc(doc(db, 'providers', app.providerUid));
            } else if (app.parentUid) {
              userDoc = await getDoc(doc(db, 'parents', app.parentUid));
            }
            if (!userDoc || !userDoc.exists()) {
              userDoc = await getDoc(doc(db, 'users', uid));
            }
            if (userDoc && userDoc.exists()) {
              const data = userDoc.data();
              namesMap[uid] = data.displayName || data.name || data.email || uid;
            } else {
              namesMap[uid] = uid;
            }
          } catch {
            namesMap[uid] = uid;
          }
        }
        setProviderNames(namesMap);
      } catch (err) {
        console.error('Error fetching applied jobs:', err);
        setErrorAppliedJobs('Failed to load applied jobs.');
      } finally {
        setLoadingAppliedJobs(false);
      }
    };
    fetchAppliedJobs();
  }, [currentUser]);
  
  // Format date and time for display
  const formatDate = date => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  const formatTime = date => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
  
  // Reusable components
  const StatCard = ({ icon: Icon, number, label, className, onClick }) => (
    <div className={`stat-card ${className} ${onClick ? 'clickable' : ''}`} onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
      <div className="stat-icon"><Icon /></div>
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
  
  const JobCard = ({ job }) => (
    <div className="job-card" key={job.id}>
      <div className="job-header">
        <h3>{job.title}</h3>
        <span className={`job-type ${job.type}`}>
          {job.type.charAt(0).toUpperCase() + job.type.slice(1)}
        </span>
      </div>
      
      <div className="job-details">
        <div className="job-info">
          <p><strong>Provider:</strong> {job.providerName}</p>
          <p><strong>Location:</strong> {job.location}</p>
          <p><strong>Rate:</strong> ${job.hourlyRate}/hour</p>
          <p><strong>Posted:</strong> {formatDate(job.postedDate)}</p>
        </div>
        
        <div className="job-actions">
          <button className="btn btn-outline btn-sm">Save Job</button>
          <Link to={`/jobseeker/jobs/${job.id}`} className="btn btn-primary btn-sm">Apply Now</Link>
        </div>
      </div>
    </div>
  );
  
  const MessagePreview = ({ avatar, sender, excerpt, time }) => (
    <div className="message-preview">
      <div className="message-avatar">{avatar}</div>
      <div className="message-content">
        <div className="message-sender">{sender}</div>
        <div className="message-excerpt">{excerpt}</div>
        <div className="message-time">{time}</div>
      </div>
    </div>
  );
  
  const DashboardCard = ({ to, icon: Icon, title, description }) => (
    <Link to={to} className="dashboard-card">
      <div className="card-content">
        <div className="card-icon"><Icon /></div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </Link>
  );
  
  // Dashboard cards configuration
  const dashboardCards = [
    {
      path: '/jobseeker/jobs', icon: FaBriefcase,
      title: 'Find Jobs', desc: 'Browse available childcare positions'
    },
    {
      path: '/jobseeker/availability', icon: FaClock,
      title: 'Availability', desc: 'Set your work schedule and availability'
    },
    {
      path: '/account/verifications', icon: FaIdCard,
      title: 'Verifications', desc: 'Manage your certifications and verifications'
    },
    {
      path: '/messages', icon: FaEnvelope,
      title: 'Messages', desc: 'View and respond to your messages'
    },
    {
      path: `/jobseeker/profile/${currentUser?.uid}`, icon: FaUserEdit,
      title: 'Edit Profile', desc: 'Update your profile and qualifications'
    }
  ];
  
  // Messages data - This would come from the API in a real application
  const messages = [];
  
  const handleActivityClick = (activity) => {
    // Navigate to messages page with the conversation ID
    navigate('/messages', { 
      state: { 
        conversationId: activity.id,
        participants: [activity.senderId, activity.receiverId]
      }
    });
  };
  
  // Profile completion logic
  const profile = userProfile || {};
  const sub = profile.profile || {};
  const isFilled = v => v !== undefined && v !== null && String(v).trim() !== '';
  const sections = [
    {
      key: 'basic',
      completed: isFilled(profile.displayName) && isFilled(profile.email) && isFilled(sub.phone),
      text: 'Basic Information'
    },
    {
      key: 'education',
      completed: isFilled(sub.education) && isFilled(sub.experience),
      text: 'Education & Experience'
    },
    {
      key: 'availability',
      completed: !!(sub.availability && Object.values(sub.availability || {}).some(day => isFilled(day.start) && isFilled(day.end))),
      text: 'Availability'
    }
  ];
  const completedCount = sections.filter(s => s.completed).length;
  const percent = Math.round((completedCount / sections.length) * 100);
  
  // Withdraw application handler
  const handleWithdraw = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) return;
    setWithdrawing(applicationId);
    try {
      await deleteDoc(doc(db, 'applications', applicationId));
      setAppliedJobs(prev => prev.filter(app => app.id !== applicationId));
    } catch (err) {
      alert('Failed to withdraw application: ' + err.message);
    } finally {
      setWithdrawing(null);
    }
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Job Seeker Dashboard</h1>
        <p className="dashboard-welcome">Welcome back, {userProfile?.displayName || 'caregiver'}!</p>
        <p className="dashboard-description">Find childcare jobs and connect with families and providers</p>
        
        <div className="profile-edit-section">
          <Link to={`/jobseeker/profile/${currentUser?.uid}`} className="edit-profile-button">
            <FaUserEdit size={16} className="icon-margin-right" />
            Edit Profile
          </Link>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="stats-grid">
        <StatCard icon={FaFileAlt} number={stats.applications} label="Applications" className="primary" />
        <StatCard icon={FaStar} number={stats.reviews} label="Reviews" className="warning" />
      </div>
      
      <div className="dashboard-layout">
        <div className="dashboard-main">
          {/* Jobs Applied Section */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Jobs Applied</h2>
              <div className="section-actions">
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/jobseeker/applications')}>View All</button>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                {loadingAppliedJobs ? (
                  <div className="loading-container"><p>Loading jobs applied...</p></div>
                ) : errorAppliedJobs ? (
                  <div className="error-message">{errorAppliedJobs}</div>
                ) : appliedJobs.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>Posted By</th>
                        <th>Applied</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appliedJobs.map(job => {
                        const isProvider = Boolean(job.providerUid);
                        const displayUid = isProvider ? job.providerUid : job.parentUid;
                        const profileType = isProvider ? 'provider' : 'parent';
                        const viewLabel = isProvider ? 'View Provider' : 'View Parent';
                        return (
                          <tr key={job.id} className="application-row">
                            <td>
                              {job.jobTitle}
                            </td>
                            <td>
                              {providerNames[displayUid] || displayUid}
                            </td>
                            <td>{job.appliedAt && (job.appliedAt.seconds ? new Date(job.appliedAt.seconds * 1000).toLocaleDateString() : new Date(job.appliedAt).toLocaleDateString())}</td>
                            <td>
                              <div className="table-actions">
                                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/jobseeker/jobs/${job.jobId}`)}>View Job</button>
                                <button 
                                  className="btn btn-outline btn-sm" 
                                  onClick={() => navigate(`/${profileType}/${displayUid}`)}
                                >
                                  {viewLabel}
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={() => handleWithdraw(job.id)} disabled={withdrawing === job.id}>
                                  {withdrawing === job.id ? 'Withdrawing...' : 'Withdraw'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state"><p>No jobs applied yet.</p></div>
                )}
              </div>
            </div>
          </section>
          
          {/* Profile Completion */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Complete Your Profile</h2>
            </div>
            
            <div className="card">
              <div className="card-body">
                <div className="profile-completion">
                  <div className="completion-progress">
                    <div className="progress-label">
                      <span>Profile Completion</span>
                      <span>{percent}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${percent}%` }}></div>
                    </div>
                  </div>
                  
                  <div className="completion-items">
                    {sections.map((item, i) => (
                      <div className={`completion-item ${item.completed ? 'completed' : ''}`} key={item.key}>
                        <div className="item-icon">{item.completed ? '✓' : '○'}</div>
                        <div className="item-text">{item.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        
        <div className="dashboard-sidebar">
          {/* Recent Activities */}
          <div className="card mt-4">
            <div className="card-header">
              <h2 className="card-title">Recent Activities</h2>
            </div>
            <div className="card-body">
              <div className="activity-feed">
                {loadingActivities ? (
                  <div className="loading-container">
                    <p>Loading activities...</p>
                  </div>
                ) : recentActivities.length > 0 ? (
                  recentActivities.map(activity => (
                    <div 
                      className="feed-item" 
                      key={activity.id}
                      onClick={() => handleActivityClick(activity)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="feed-icon">{activity.icon}</div>
                      <div className="feed-content">
                        <div className="feed-title">
                          {activity.senderId === currentUser.uid 
                            ? `You sent a message to ${activity.receiverName}`
                            : `${activity.senderName} sent you a message`}
                        </div>
                        <div className="feed-time">{activity.timestamp && (new Date(activity.timestamp.seconds ? activity.timestamp.seconds * 1000 : activity.timestamp)).toLocaleDateString()}</div>
                        {activity.content && (
                          <div className="feed-message">{activity.content}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="feed-item">
                    <div className="feed-icon">🎉</div>
                    <div className="feed-content">
                      <div className="feed-title">Welcome to your dashboard!</div>
                      <div className="feed-time">Just now</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="dashboard-quick-actions">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              <Link to="/jobseeker/jobs" className="quick-action-card">
                <div className="quick-action-icon"><FaBriefcase /></div>
                <div className="quick-action-content">
                  <h3>Find Jobs</h3>
                  <p>Browse available opportunities</p>
                </div>
              </Link>
              
              <Link to="/jobseeker/search/providers" className="quick-action-card">
                <div className="quick-action-icon"><FaMapMarkerAlt /></div>
                <div className="quick-action-content">
                  <h3>Find Childcare Providers</h3>
                  <p>Browse daycare centers and homes</p>
                </div>
              </Link>
              
              <Link to="/jobseeker/profile" className="quick-action-card">
                <div className="quick-action-icon"><FaUserEdit /></div>
                <div className="quick-action-content">
                  <h3>Update Profile</h3>
                  <p>Keep your information current</p>
                </div>
              </Link>
              
              <Link to="/messages" className="quick-action-card">
                <div className="quick-action-icon"><FaEnvelope /></div>
                <div className="quick-action-content">
                  <h3>Messages</h3>
                  <p>Check your conversations</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerDashboard;